import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Avatar } from '@/components/admin/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { SuspendModal, BlockModal } from '@/components/admin/AccountActionModal';

type Tag = 'SELLER' | 'BUYER' | 'PAYMENTS' | 'ORDERS' | 'SECURITY';
interface TimelineEntry {
  id: string; at: string; tags: Tag[]; activity: string;
  relatedItem?: string; amountGBP?: number; status?: string;
}
interface ListingRow { id: string; title: string; state: string; views: number; saves: number; priceGBP: number; createdAt: string }
interface SellerStats {
  totalListings: number; activeListings: number; soldListings: number;
  totalViews: number; totalSaves: number; avgViewsPerListing: number;
  proposalsReceived: number; proposalsAccepted: number; proposalsRejected: number;
  sellThroughRate: number | null; avgTimeToSellDays: number | null;
  totalSalesValueGBP: number; cancelledOrders: number; returnsOnSales: number;
  listings: ListingRow[];
}
interface BuyerStats {
  proposalsMade: number; proposalsAccepted: number; proposalsRejected: number;
  purchases: number; avgOrderValueGBP: number; purchaseFrequencyPerMonth: number;
  cancellations: number; returnsFiled: number;
  productsSaved: number; productsViewedApprox: number;
  mostViewedCategories: { name: string; count: number }[];
  mostViewedBrands: { name: string; count: number }[];
  swapActivity: Record<string, number>;
  membershipUsage: {
    aiListings: { used: number; limit: number };
    listings: { used: number; limit: number | null };
    completeLook: { used: number; limit: number };
  };
}
interface ComplaintRow {
  id: string; direction: 'made' | 'against'; orderId?: string | null; orderRef?: string | null;
  category: string; notes?: string | null; state: 'OPEN' | 'RESOLVED' | 'DISMISSED';
  resolution?: string | null; createdAt: string; resolvedAt?: string | null; counterpartyName?: string | null;
}
interface Insight { observation: string; evidence: string }
interface Detail {
  id: string; name: string; email: string; role: string; handle?: string | null;
  accountStatus: 'ACTIVE' | 'SUSPENDED' | 'BLOCKED'; statusReason?: string | null;
  suspendedUntil?: string | null; releaseAt?: string | null; suspensionReviewDue: boolean;
  idVerification: string; twoFactorEnabled: boolean;
  membershipTier: string; membershipExpiresAt?: string | null;
  proTier?: string | null; proExpiresAt?: string | null;
  trustScore: number; trustTier: string; createdAt: string; lastLoginAt?: string | null;
  ratingAvg: number; ratingCount: number;
  stripeChargesEnabled: boolean; stripePayoutsEnabled: boolean;
  totals: { listings: number; purchases: number; sales: number; refunds: number };
  sellerWalletGBP: number; buyerCreditGBP: number;
  timeline: TimelineEntry[];
  sellerStats: SellerStats; buyerStats: BuyerStats;
  complaintsDetailed: ComplaintRow[];
  insights: Insight[];
}

const TABS = ['Overview', 'Seller', 'Buyer', 'Activity', 'Complaints', 'Security'] as const;
type TabKey = typeof TABS[number];

const ACTIVITY_FILTERS: { key: 'ALL' | Tag; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'SELLER', label: 'Seller' },
  { key: 'BUYER', label: 'Buyer' },
  { key: 'PAYMENTS', label: 'Payments' },
  { key: 'ORDERS', label: 'Orders' },
];

const TRUST_PILL: Record<string, string> = { GOLD: 'pill-gold', TRUSTED: 'pill-ok', STANDARD: 'bg-cream-deep text-mute', WATCH: 'pill-bad' };

export default function AdminUserDetailPage() {
  const { id: userId } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabKey>('Overview');
  const [activityFilter, setActivityFilter] = useState<'ALL' | Tag>('ALL');
  const [modal, setModal] = useState<'suspend' | 'block' | null>(null);

  const { data, isLoading } = useQuery<Detail>({
    queryKey: ['admin', 'user', userId],
    queryFn: async () => (await api.get(`/admin/users/${userId}`)).data,
    enabled: !!userId,
  });

  const act = useMutation({
    mutationFn: async (action: string) => (await api.post(`/admin/users/${userId}/${action}`)).data,
    onSuccess: () => {
      toast.success('Done.');
      qc.invalidateQueries({ queryKey: ['admin', 'user', userId] });
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Action failed.'),
  });

  const resolveComplaint = useMutation({
    mutationFn: async ({ id, state, resolution }: { id: string; state: string; resolution?: string }) =>
      (await api.post(`/admin/complaints/${id}/resolve`, { state, resolution })).data,
    onSuccess: () => {
      toast.success('Complaint updated.');
      qc.invalidateQueries({ queryKey: ['admin', 'user', userId] });
    },
    onError: () => toast.error('Could not update complaint.'),
  });

  if (isLoading || !data) {
    return (
      <div className="p-6 md:p-10 space-y-3">
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-10 skeleton rounded" />)}
      </div>
    );
  }

  const timeline = data.timeline.filter((e) => activityFilter === 'ALL' || e.tags.includes(activityFilter));
  const securityTimeline = data.timeline.filter((e) => e.tags.includes('SECURITY'));

  return (
    <div className="p-6 md:p-10">
      {/* Signature header */}
      <div className="section-dark relative overflow-hidden bg-ink text-cream rounded-bold p-7 md:p-8 shadow-luxe">
        <div className="absolute inset-0 bg-gold-mesh opacity-40" />
        <div className="absolute inset-0 bg-dot-light bg-[length:24px_24px] opacity-25" />
        <div className="relative">
          <Link to="/control-7f3a9b2c/users" className="mono text-[10px] tracking-[0.14em] uppercase text-cream/45 hover:text-gold-light transition">← Back to users</Link>
          <div className="flex flex-wrap items-start justify-between gap-5 mt-4">
            <div className="flex items-center gap-4 min-w-0">
              <Avatar name={data.name} tone="gold" className="w-14 h-14 text-xl" />
              <div className="min-w-0">
                <div className="mono text-[10px] tracking-[0.28em] uppercase text-gold-light/80">User · {data.role}{data.accountStatus !== 'ACTIVE' ? ` · ${data.accountStatus}` : ''}</div>
                <h1 className="display text-4xl md:text-5xl mt-1.5 leading-none truncate">{data.name}</h1>
                <div className="text-cream/60 text-[13px] mt-2">{data.email}{data.handle ? ` · @${data.handle}` : ''}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {data.idVerification !== 'APPROVED' && <button onClick={() => act.mutate('verify')} disabled={act.isPending} className="btn btn-sm btn-outline-cream text-[11px] py-1 px-2.5">Verify ID</button>}
              {data.accountStatus !== 'ACTIVE' ? (
                <>
                  <button onClick={() => act.mutate('reinstate')} disabled={act.isPending} className="btn btn-sm btn-gold !text-ink text-[11px] py-1 px-2.5">Reinstate</button>
                  {data.accountStatus === 'SUSPENDED' && (
                    <button onClick={() => setModal('block')} className="btn btn-sm text-[11px] py-1 px-2.5 bg-cream/10 text-cream hover:bg-cream/20">Escalate to Block</button>
                  )}
                </>
              ) : data.role !== 'ADMIN' ? (
                <>
                  <button onClick={() => setModal('suspend')} className="btn btn-sm text-[11px] py-1 px-2.5 bg-bad text-cream hover:opacity-90">Suspend</button>
                  <button onClick={() => setModal('block')} className="btn btn-sm btn-outline-cream text-[11px] py-1 px-2.5">Block</button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {data.accountStatus !== 'ACTIVE' && (
        <div className="rounded-luxe border border-bad/30 bg-bad/5 p-3 text-[13px] mt-4">
          <div className="stamp text-bad text-[9px]">{data.accountStatus}</div>
          {data.statusReason && <div className="mt-1">{data.statusReason}</div>}
          {data.accountStatus === 'SUSPENDED' && data.suspendedUntil && (
            <div className="text-[12px] text-mute mt-1">
              30-day period {data.suspensionReviewDue ? 'has elapsed - ' : 'ends '}
              {new Date(data.suspendedUntil).toLocaleDateString()}
              {data.suspensionReviewDue && ' - decide whether to reinstate or escalate to a block.'}
            </div>
          )}
          {data.accountStatus === 'BLOCKED' && data.releaseAt && (
            <div className="text-[12px] text-mute mt-1">Appeal approved - restores automatically on {new Date(data.releaseAt).toLocaleDateString()}.</div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="inline-flex bg-cream-deep rounded-full p-1 flex-wrap mt-6">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 text-[12px] rounded-full transition ${tab === t ? 'bg-ink text-cream' : 'text-mute hover:text-ink'}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === 'Overview' && <OverviewTab data={data} />}
        {tab === 'Seller' && <SellerTab s={data.sellerStats} />}
        {tab === 'Buyer' && <BuyerTab b={data.buyerStats} />}
        {tab === 'Activity' && (
          <ActivityTab entries={timeline} filter={activityFilter} setFilter={setActivityFilter} />
        )}
        {tab === 'Complaints' && (
          <ComplaintsTab
            rows={data.complaintsDetailed}
            busy={resolveComplaint.isPending}
            onResolve={(id, state, resolution) => resolveComplaint.mutate({ id, state, resolution })}
          />
        )}
        {tab === 'Security' && <SecurityTab data={data} entries={securityTimeline} />}
      </div>

      {modal === 'suspend' && <SuspendModal userId={data.id} userName={data.name} onClose={() => setModal(null)} />}
      {modal === 'block' && <BlockModal userId={data.id} userName={data.name} onClose={() => setModal(null)} />}
    </div>
  );
}

/* ───────────────────────── Overview ───────────────────────── */

function OverviewTab({ data }: { data: Detail }) {
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-4 gap-3 text-[13px]">
        <Box k="Verification" v={data.idVerification} sub={data.twoFactorEnabled ? '2FA on' : '2FA off'} />
        <Box k="Trust" v={`${data.trustScore}/100`} sub={data.trustTier} />
        <Box k="Membership" v={data.membershipTier} sub={data.membershipExpiresAt ? `Until ${new Date(data.membershipExpiresAt).toLocaleDateString()}` : undefined} />
        <Box k="Souqli Pro" v={data.proTier ?? 'FREE'} sub={data.proExpiresAt ? `Until ${new Date(data.proExpiresAt).toLocaleDateString()}` : undefined} />
        <Box k="Joined" v={new Date(data.createdAt).toLocaleDateString()} />
        <Box k="Last activity" v={data.lastLoginAt ? new Date(data.lastLoginAt).toLocaleDateString() : 'Never logged in'} />
        <Box k="Rating" v={data.ratingCount ? `${data.ratingAvg.toFixed(1)}★` : '-'} sub={data.ratingCount ? `${data.ratingCount} reviews` : undefined} />
        <Box k="Payouts" v={data.stripePayoutsEnabled ? 'Enabled' : 'Not set up'} sub={data.stripeChargesEnabled ? undefined : 'Charges not enabled'} />
      </div>
      <div className="grid sm:grid-cols-4 gap-3 text-[13px]">
        <Box k="Listings" v={String(data.totals.listings)} />
        <Box k="Sales" v={String(data.totals.sales)} sub={`£${data.sellerWalletGBP.toFixed(2)} wallet`} />
        <Box k="Purchases" v={String(data.totals.purchases)} sub={`£${data.buyerCreditGBP.toFixed(2)} credit`} />
        <Box k="Refunds" v={String(data.totals.refunds)} />
      </div>
      <div className="flex flex-wrap gap-2">
        <span className={`pill text-[9px] ${TRUST_PILL[data.trustTier] ?? ''}`}>{data.trustTier}</span>
      </div>

      <div>
        <div className="stamp text-gold-dark text-[9px] mb-2">ADMIN INSIGHTS</div>
        {data.insights.length ? (
          <div className="space-y-2">
            {data.insights.map((i, idx) => (
              <div key={idx} className="rounded-luxe border border-warmline p-3.5 text-[13px]">
                <div className="text-ink">{i.observation}</div>
                <div className="text-[11px] text-mute mt-1">{i.evidence}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card p-5 text-mute text-[13px]">Nothing notable yet - no thresholds crossed in this account's recorded activity.</div>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────── Seller ───────────────────────── */

function SellerTab({ s }: { s: SellerStats }) {
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-4 gap-3 text-[13px]">
        <Box k="Total listings" v={String(s.totalListings)} />
        <Box k="Active" v={String(s.activeListings)} />
        <Box k="Sold" v={String(s.soldListings)} />
        <Box k="Sell-through rate" v={s.sellThroughRate != null ? `${(s.sellThroughRate * 100).toFixed(0)}%` : '-'} />
        <Box k="Total views" v={String(s.totalViews)} />
        <Box k="Total saves" v={String(s.totalSaves)} />
        <Box k="Avg views / listing" v={s.avgViewsPerListing.toFixed(1)} />
        <Box k="Avg time to sell" v={s.avgTimeToSellDays != null ? `${s.avgTimeToSellDays.toFixed(1)} days` : '-'} />
        <Box k="Offers received" v={String(s.proposalsReceived)} sub={`${s.proposalsAccepted} accepted · ${s.proposalsRejected} declined`} />
        <Box k="Total sales value" v={`£${s.totalSalesValueGBP.toFixed(2)}`} />
        <Box k="Cancelled orders" v={String(s.cancelledOrders)} />
        <Box k="Returns on sales" v={String(s.returnsOnSales)} />
      </div>

      <div>
        <div className="stamp text-gold-dark text-[9px] mb-2">INDIVIDUAL LISTING PERFORMANCE</div>
        <div className="card overflow-x-auto">
          {s.listings.length ? (
            <div className="min-w-[480px]">
              {s.listings.map((l) => (
                <div key={l.id} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-3 px-4 py-2.5 text-[12px] border-b border-warmline last:border-0 items-center">
                  <div className="truncate">{l.title}</div>
                  <span className="pill bg-cream-deep text-mute text-[9px] justify-self-start">{l.state}</span>
                  <div className="text-mute">{l.views} views</div>
                  <div className="text-mute">{l.saves} saves</div>
                  <div className="mono text-right">£{l.priceGBP.toFixed(2)}</div>
                </div>
              ))}
            </div>
          ) : <div className="px-5 py-8 text-center text-mute text-[12px]">No listings.</div>}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Buyer ───────────────────────── */

function QuotaBar({ label, used, limit }: { label: string; used: number; limit: number | null }) {
  return (
    <div className="rounded-luxe border border-warmline bg-white p-3.5">
      <div className="flex items-baseline justify-between">
        <div className="text-[13px] font-semibold text-ink">{label}</div>
        <div className="text-[13px] text-mute">{limit == null ? `${used} · Unlimited` : `${used} / ${limit}`}</div>
      </div>
      <div className="h-1.5 rounded-full bg-cream-deep overflow-hidden mt-2">
        <div className="h-full bg-gold rounded-full transition-all" style={{ width: limit == null ? '100%' : `${Math.min(100, (used / limit) * 100)}%` }} />
      </div>
    </div>
  );
}

function BuyerTab({ b }: { b: BuyerStats }) {
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-4 gap-3 text-[13px]">
        <Box k="Purchases" v={String(b.purchases)} />
        <Box k="Avg order value" v={`£${b.avgOrderValueGBP.toFixed(2)}`} />
        <Box k="Purchase frequency" v={`${b.purchaseFrequencyPerMonth.toFixed(2)} / month`} />
        <Box k="Cancellations" v={String(b.cancellations)} />
        <Box k="Returns filed" v={String(b.returnsFiled)} />
        <Box k="Offers made" v={String(b.proposalsMade)} sub={`${b.proposalsAccepted} accepted · ${b.proposalsRejected} declined`} />
        <Box k="Products saved" v={String(b.productsSaved)} />
        <Box k="Products viewed" v={`~${b.productsViewedApprox}`} sub="Recorded activity only" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <div className="stamp text-gold-dark text-[9px] mb-2">MOST-VIEWED CATEGORIES</div>
          <TallyList rows={b.mostViewedCategories} />
        </div>
        <div>
          <div className="stamp text-gold-dark text-[9px] mb-2">MOST-VIEWED BRANDS</div>
          <TallyList rows={b.mostViewedBrands} />
        </div>
      </div>
      <p className="text-[11px] text-mute -mt-3">Based on saved/viewed activity recorded on Souqli - full browsing history isn't tracked server-side yet.</p>

      <div>
        <div className="stamp text-gold-dark text-[9px] mb-2">SWAP ACTIVITY</div>
        {Object.keys(b.swapActivity).length ? (
          <div className="flex flex-wrap gap-2">
            {Object.entries(b.swapActivity).map(([state, count]) => (
              <span key={state} className="pill bg-cream-deep text-mute text-[9px]">{state}: {count}</span>
            ))}
          </div>
        ) : <div className="text-mute text-[12px]">No swap activity.</div>}
      </div>

      <div>
        <div className="stamp text-gold-dark text-[9px] mb-2">MEMBERSHIP &amp; AI FEATURE USAGE (THIS MONTH)</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <QuotaBar label="AI-styled listings" used={b.membershipUsage.aiListings.used} limit={b.membershipUsage.aiListings.limit} />
          <QuotaBar label="Listings (Pro cap)" used={b.membershipUsage.listings.used} limit={b.membershipUsage.listings.limit} />
          <QuotaBar label="Complete the Look" used={b.membershipUsage.completeLook.used} limit={b.membershipUsage.completeLook.limit} />
        </div>
      </div>
    </div>
  );
}

function TallyList({ rows }: { rows: { name: string; count: number }[] }) {
  if (!rows.length) return <div className="text-mute text-[12px]">No data yet.</div>;
  return (
    <div className="card overflow-hidden">
      {rows.map((r) => (
        <div key={r.name} className="flex items-center justify-between px-4 py-2 text-[12px] border-b border-warmline last:border-0">
          <span className="truncate">{r.name}</span>
          <span className="text-mute">{r.count}</span>
        </div>
      ))}
    </div>
  );
}

/* ───────────────────────── Activity ───────────────────────── */

function ActivityTab({ entries, filter, setFilter }: { entries: TimelineEntry[]; filter: 'ALL' | Tag; setFilter: (t: 'ALL' | Tag) => void }) {
  return (
    <div className="space-y-4">
      <div className="inline-flex bg-cream-deep rounded-full p-1 flex-wrap">
        {ACTIVITY_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3.5 py-1.5 text-[12px] rounded-full transition ${filter === f.key ? 'bg-ink text-cream' : 'text-mute hover:text-ink'}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <Timeline entries={entries} />
    </div>
  );
}

function Timeline({ entries }: { entries: TimelineEntry[] }) {
  return (
    <div className="rounded-luxe border border-warmline overflow-x-auto">
      {entries.length ? (
        <div className="min-w-[380px]">
          {entries.map((e) => (
            <div key={e.id} className="grid grid-cols-[auto_1fr_auto] gap-3 px-4 py-2.5 text-[12px] border-b border-warmline last:border-0 items-center">
              <div className="text-mute mono text-[10px] whitespace-nowrap">
                {new Date(e.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} · {new Date(e.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className="min-w-0">
                <span className="text-ink">{e.activity}</span>
                {e.relatedItem && <span className="text-mute"> - {e.relatedItem}</span>}
                {e.status && <span className="text-mute text-[11px]"> ({e.status})</span>}
              </div>
              <div className="text-right shrink-0">
                {e.amountGBP != null && <span className={`mono ${e.amountGBP < 0 ? 'text-bad' : 'text-ok'}`}>{e.amountGBP < 0 ? '−' : ''}£{Math.abs(e.amountGBP).toFixed(2)}</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="px-5 py-10 text-center text-mute text-[12px]">No activity in this category.</div>
      )}
    </div>
  );
}

/* ───────────────────────── Complaints ───────────────────────── */

function ComplaintsTab({ rows, busy, onResolve }: { rows: ComplaintRow[]; busy: boolean; onResolve: (id: string, state: string, resolution?: string) => void }) {
  const [notes, setNotes] = useState<Record<string, string>>({});
  const made = rows.filter((r) => r.direction === 'made');
  const against = rows.filter((r) => r.direction === 'against');

  const Row = ({ c }: { c: ComplaintRow }) => (
    <div className="p-4 border-b border-warmline last:border-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] text-mute mono">{c.orderRef ?? 'No order'} · {new Date(c.createdAt).toLocaleDateString()}</div>
          <div className="text-[13px] mt-0.5">{c.category}{c.counterpartyName ? ` - ${c.direction === 'made' ? 'against' : 'from'} ${c.counterpartyName}` : ''}</div>
          {c.notes && <p className="text-[12px] text-mute mt-1">"{c.notes}"</p>}
          {c.resolution && <p className="text-[12px] text-ok mt-1">Resolution: {c.resolution}</p>}
        </div>
        <span className={`pill text-[9px] shrink-0 ${c.state === 'OPEN' ? 'pill-bad' : c.state === 'RESOLVED' ? 'pill-ok' : 'bg-cream-deep text-mute'}`}>{c.state}</span>
      </div>
      {c.direction === 'against' && c.state === 'OPEN' && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            value={notes[c.id] ?? ''}
            onChange={(e) => setNotes((n) => ({ ...n, [c.id]: e.target.value }))}
            placeholder="Resolution note (optional)"
            className="input text-[12px] py-1.5 flex-1 min-w-[160px]"
          />
          <button onClick={() => onResolve(c.id, 'DISMISSED', notes[c.id])} disabled={busy} className="btn btn-sm btn-outline">Dismiss</button>
          <button onClick={() => onResolve(c.id, 'RESOLVED', notes[c.id])} disabled={busy} className="btn btn-sm btn-gold !text-ink">Mark resolved</button>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <div className="stamp text-gold-dark text-[9px] mb-2">AGAINST THIS USER ({against.length})</div>
        <div className="card overflow-hidden">
          {against.length ? against.map((c) => <Row key={c.id} c={c} />) : <div className="px-5 py-8 text-center text-mute text-[12px]">None.</div>}
        </div>
      </div>
      <div>
        <div className="stamp text-gold-dark text-[9px] mb-2">FILED BY THIS USER ({made.length})</div>
        <div className="card overflow-hidden">
          {made.length ? made.map((c) => <Row key={c.id} c={c} />) : <div className="px-5 py-8 text-center text-mute text-[12px]">None.</div>}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Security ───────────────────────── */

function SecurityTab({ data, entries }: { data: Detail; entries: TimelineEntry[] }) {
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-4 gap-3 text-[13px]">
        <Box k="Trust score" v={`${data.trustScore}/100`} sub={data.trustTier} />
        <Box k="2FA" v={data.twoFactorEnabled ? 'Enabled' : 'Disabled'} />
        <Box k="ID verification" v={data.idVerification} />
        <Box k="Account status" v={data.accountStatus} sub={data.statusReason ?? undefined} />
      </div>
      <div>
        <div className="stamp text-gold-dark text-[9px] mb-2">SECURITY &amp; TRUST HISTORY</div>
        <Timeline entries={entries} />
      </div>
    </div>
  );
}

/* ───────────────────────── Shared ───────────────────────── */

function Box({ k, v, sub }: { k: string; v: string; sub?: string }) {
  return (
    <div className="rounded-luxe border border-warmline p-3">
      <div className="stamp text-mute text-[9px]">{k}</div>
      <div className="font-medium truncate">{v}</div>
      {sub && <div className="text-[11px] text-mute truncate">{sub}</div>}
    </div>
  );
}
